import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';

import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { FilterProvider } from 'src/components/app/context/FilterContext';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import type { PageFrameProps } from 'src/components/common/PageFrame';
import PageFrame from 'src/components/common/PageFrame';
import { ShoppingCartProvider } from 'src/components/report/ShoppingCartContext';
import { useReportConfigAllocationCombinationQuery } from 'src/components/reportConfig/hooks/useReportConfigAllocationQuery';
import WorkspaceAccordion from 'src/components/workspace/components/WorkspaceAccordion';
import WorkspaceActionBanner from 'src/components/workspace/components/WorkspaceActionBanner';
import WorkspaceContent from 'src/components/workspace/components/WorkspaceContent';
import { getReportConfiguration } from 'src/components/workspace/helper/helper';
import { useWorkspaceContext } from 'src/components/workspace/WorkspaceContext';

export interface WorkspacePageProps extends PageFrameProps {}

export const WORKSPACE_LOADER = 'workspace-loader';
export const WORKSPACE_NO_REPORTS = 'workspace-no-reports';

const WorkspacePage: FC<WorkspacePageProps> = () => {
  const { t } = useTranslation('workspace');
  const { user } = useUserContext();
  const {
    getCustomReports,
    getDatalineTemplates,
    setReportConfiguration,
    hasDefaultReportConfiguration,
    currentReport,
  } = useWorkspaceContext();
  const { moduleId } = useSection();
  const {
    data: reportConfigData,
    loading,
    fetched,
  } = useReportConfigAllocationCombinationQuery(moduleId);
  const [isDefaultConfiguration, setIsDefaultConfiguration] = useState(
    hasDefaultReportConfiguration(),
  );

  useEffect(() => {
    getCustomReports({ reset: true });
    getDatalineTemplates(true);
  }, []);

  useEffect(() => {
    // ensure setting a valid current report configuration when available
    if (
      hasDefaultReportConfiguration() &&
      !loading &&
      fetched &&
      Object.keys(reportConfigData || {}).length
    ) {
      const newReportConfiguration = getReportConfiguration(
        currentReport,
        reportConfigData,
      );
      if (newReportConfiguration) {
        setReportConfiguration(newReportConfiguration, () => {});
        setIsDefaultConfiguration(false);
      }
    }
  }, [loading, fetched, reportConfigData, currentReport]);

  if (user && !user.pro_terms_accepted) {
    history.push(routePaths.TERMS_OF_USAGE);

    return null;
  }

  return (
    <PageFrame
      sidebar={<WorkspaceAccordion />}
      actionBanner={<WorkspaceActionBanner />}
    >
      {loading || !fetched ? (
        <Box
          data-test-id={WORKSPACE_LOADER}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : !isDefaultConfiguration ? (
        <WorkspaceContent />
      ) : (
        <Box
          data-test-id={WORKSPACE_NO_REPORTS}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {t('reportForm.noReportTypesAvailable')}
        </Box>
      )}
    </PageFrame>
  );
};

// Wrap this page in a separate FilterProvider as its call of the useOrgTreeState-hook would run into racing conditions
// with the global initialization of the PlanActionContext and its call of the useOrgTreeState-hook.
const WorkspacePageWrapper: FC<WorkspacePageProps> = ({ ...props }) => (
  <FilterProvider>
    <ShoppingCartProvider>
      <WorkspacePage {...props} />
    </ShoppingCartProvider>
  </FilterProvider>
);

export default WorkspacePageWrapper;
